import axios from 'axios';

class EventManager {
    constructor() {
        this.events = {};
        this.selectedEventId = null;
        this.originalEvents = {};
        this.hasChanges = false;
        this.lastSyncTime = null;
        this.remoteSha = null;
        this.autoSyncInterval = null;
        this.githubAPI = new GitHubAPI();
    }

    // Load events from GitHub API or fallback to local
    async loadEvents(forceSync = false) {
        try {
            // Always attempt to sync with remote first
            const syncResult = await this.githubAPI.syncWithRemote();
            
            if (syncResult.success && syncResult.events) {
                // Successfully synced with remote
                this.originalEvents = { ...syncResult.events };
                this.events = { ...syncResult.events };
                this.remoteSha = syncResult.sha;
                this.lastSyncTime = new Date();
                this.showNotification('Events synced from GitHub repository', 'success');
                console.log('Events loaded from GitHub:', Object.keys(this.events).length, 'events');
            } else {
                // Fallback to local events.json from nssc_admin
                console.log('Falling back to local events file');
                try {
                    const response = await import('../data/events.json');
                    this.events = eventsArrayToMap(response.default);
                    this.showNotification(syncResult.message || 'Using local events data', 'warning');
                } catch (localError) {
                    console.error('Failed to load local events:', localError);
                    this.events = {};
                    this.showNotification('No events data available. Please configure GitHub access or add local events.', 'error');
                }
                this.originalEvents = { ...this.events };
                this.showNotification('Loaded from local file. Connect to GitHub for latest updates.', 'info');
                console.log('Events loaded from local file');
            }
            
            // Start auto-sync if authenticated
            this.startAutoSync();
            
            return this.events;
        } catch (error) {
            console.error('Error loading events:', error);
            this.showNotification(`Failed to load events: ${error.message}`, 'error');
            // Last resort: use empty events
            this.events = {};
            this.originalEvents = {};
            return {};
        }
    }

    // Start automatic sync checking
    startAutoSync() {
        if (this.autoSyncInterval) {
            clearInterval(this.autoSyncInterval);
        }

        if (this.githubAPI.isAuthenticated()) {
            // Check for remote changes every 30 seconds
            this.autoSyncInterval = setInterval(async () => {
                try {
                    const result = await this.githubAPI.checkForRemoteChanges(this.remoteSha);
                    if (result.hasChanges) {
                        this.showSyncNotification();
                    }
                } catch (error) {
                    console.error('Auto-sync check failed:', error);
                }
            }, 30000);
        }
    }

    // Stop automatic sync
    stopAutoSync() {
        if (this.autoSyncInterval) {
            clearInterval(this.autoSyncInterval);
            this.autoSyncInterval = null;
        }
    }

    // Show notification (to be replaced with React Toast notifications)
    showNotification(message, type = 'info') {
        console.log(`${type.toUpperCase()}: ${message}`);
        // This will be replaced with actual toast notifications in React
    }

    // Show sync notification
    showSyncNotification() {
        this.showNotification('Remote changes detected. Please sync to get latest updates.', 'warning');
    }

    // Get all events
    getEvents() {
        return this.events;
    }

    // Get single event by ID
    getEvent(eventId) {
        return this.events[eventId] || null;
    }

    // Add or update event
    updateEvent(eventId, eventData) {
        this.events[eventId] = { ...eventData };
        this.hasChanges = true;
        this.showNotification(`Event ${eventData.name} updated successfully`, 'success');
        return this.events[eventId];
    }

    // Delete event
    deleteEvent(eventId) {
        if (this.events[eventId]) {
            const eventName = this.events[eventId].name;
            delete this.events[eventId];
            this.hasChanges = true;
            this.showNotification(`Event ${eventName} deleted successfully`, 'success');
            return true;
        }
        return false;
    }

    // Check if there are unsaved changes
    hasUnsavedChanges() {
        return this.hasChanges;
    }

    // Save changes to GitHub
    async saveChanges(commitMessage = 'Update events data') {
        if (!this.hasChanges) {
            return { success: false, noChanges: true, message: 'No changes to save' };
        }

        try {
            const result = await this.githubAPI.createPullRequest(
                this.events,
                commitMessage,
                'Update events via dashboard'
            );

            if (result.success) {
                this.hasChanges = false;
                this.originalEvents = { ...this.events };
            }
            return result;
        } catch (error) {
            console.error('Error saving changes:', error);
            return {
                success: false,
                message: error.message || 'Failed to save changes',
            };
        }
    }

    // Sync with remote (pull latest changes)
    async syncWithRemote() {
        try {
            const result = await this.loadEvents(true);
            if (result) {
                this.showNotification('Successfully synced with remote repository', 'success');
                return true;
            }
            return false;
        } catch (error) {
            console.error('Sync failed:', error);
            this.showNotification(`Sync failed: ${error.message}`, 'error');
            return false;
        }
    }
}

// The events file on disk is an ARRAY of event objects. In memory we index by
// slug so the existing per-event get/update/delete paths (which used numeric
// ids) still work with a string key. These helpers marshal between the two.
function eventsArrayToMap(arr) {
    const map = {};
    if (Array.isArray(arr)) {
        for (const event of arr) {
            if (event && typeof event === 'object' && event.slug) {
                map[event.slug] = event;
            }
        }
    } else if (arr && typeof arr === 'object') {
        // Backwards-compat: if the file was still keyed (previous shape), pass through.
        return arr;
    }
    return map;
}

function eventsMapToArray(map) {
    return Object.values(map || {});
}

// Project the unified in-memory shape → backend catalog shape
// (nssc_backend/data/events.json). Backend keys by numeric backendId (as
// string), only cares about a subset of fields, ignores everything display.
function projectToBackendShape(eventsMap) {
    const out = {};
    for (const event of Object.values(eventsMap || {})) {
        if (!event || event.backendId == null) continue;
        const key = String(event.backendId);
        const entry = {
            slug: event.slug,
            name: event.name,
            path: `/${event.slug}`,
            team: !!event.team,
            isSub: false,
            deadline: event.submissiondate || '',
            registrationStarted: !!event.registrationStarted,
        };
        if (event.team) {
            entry.min = Number(event.min);
            entry.max = Number(event.max);
        }
        out[key] = entry;
    }
    // Sort keys numerically for a stable diff on GitHub.
    const sorted = {};
    Object.keys(out)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((k) => { sorted[k] = out[k]; });
    return sorted;
}

// Project the unified in-memory shape → admin minimal catalog shape
// (nssc_admin/src/data/events-catalog.json). The admin panel only needs
// enough to render the "Event Registrations" dropdown and page header:
// backendId key → { name, isSub, team, [min, max] }. No slug/deadline/desc.
function projectToAdminShape(eventsMap) {
    const out = {};
    for (const event of Object.values(eventsMap || {})) {
        if (!event || event.backendId == null) continue;
        const key = String(event.backendId);
        const entry = {
            name: event.name,
            isSub: !!event.isSub,
            team: !!event.team,
        };
        if (event.team) {
            entry.min = Number(event.min);
            entry.max = Number(event.max);
        }
        out[key] = entry;
    }
    const sorted = {};
    Object.keys(out)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((k) => { sorted[k] = out[k]; });
    return sorted;
}

class GitHubAPI {
    constructor() {
        // Primary (frontend) target
        this.owner = process.env.REACT_APP_GITHUB_OWNER || 'NSSC-2026';
        this.repo = process.env.REACT_APP_GITHUB_REPO || 'nssc-2026';
        this.filePath = process.env.REACT_APP_GITHUB_FILE_PATH || 'src/lib/data/events-data.json';
        this.branch = process.env.REACT_APP_GITHUB_BRANCH || 'backend';

        // Secondary (backend) target — separate owner/repo/path/branch.
        // Defaults to the same owner; override for a different org.
        this.backendOwner = process.env.REACT_APP_GITHUB_BACKEND_OWNER || this.owner;
        this.backendRepo = process.env.REACT_APP_GITHUB_BACKEND_REPO || 'nssc_backend';
        this.backendFilePath = process.env.REACT_APP_GITHUB_BACKEND_FILE_PATH || 'data/events.json';
        this.backendBranch = process.env.REACT_APP_GITHUB_BACKEND_BRANCH || 'main';

        // Tertiary (admin) target — the admin panel's own minimal catalog.
        // Same GitHub API mechanics; just a third file with an even smaller shape.
        this.adminOwner = process.env.REACT_APP_GITHUB_ADMIN_OWNER || this.owner;
        this.adminRepo = process.env.REACT_APP_GITHUB_ADMIN_REPO || 'nssc_admin';
        this.adminFilePath = process.env.REACT_APP_GITHUB_ADMIN_FILE_PATH || 'src/data/events-catalog.json';
        this.adminBranch = process.env.REACT_APP_GITHUB_ADMIN_BRANCH || 'main';

        this.token = process.env.REACT_APP_GITHUB_TOKEN || null;
        this.baseURL = 'https://api.github.com';
        this.fallbackToken = null; // For manual token input
    }

    // Three file targets each save touches. Owner is per-target so the frontend,
    // backend, and admin panel can live under different GitHub orgs.
    _frontendTarget() {
        return { owner: this.owner, repo: this.repo, branch: this.branch, path: this.filePath, label: 'frontend' };
    }
    _backendTarget() {
        return { owner: this.backendOwner, repo: this.backendRepo, branch: this.backendBranch, path: this.backendFilePath, label: 'backend' };
    }
    _adminTarget() {
        return { owner: this.adminOwner, repo: this.adminRepo, branch: this.adminBranch, path: this.adminFilePath, label: 'admin' };
    }


    getToken() {
        
        if (this.token) {
            return this.token;
        }

    }

    // Check if authenticated
    isAuthenticated() {
        return !!this.getToken();
    }

    // Get headers for API requests
    getHeaders() {
        const token = this.getToken();
        if (!token) {
            throw new Error('GitHub token not set. Please authenticate first.');
        }
        
        return {
            'Authorization': `token ${token}`,
            'Accept': 'application/vnd.github.v3+json',
            'Content-Type': 'application/json'
        };
    }

    // Get current file content + SHA at a target (defaults to frontend).
    // Only the frontend file goes through eventsArrayToMap — the backend file
    // has a different shape (object keyed by id) and we never edit it in-place,
    // so callers that need its SHA don't need its parsed content.
    async getFileContent(target = null) {
        const t = target || this._frontendTarget();
        try {
            if (!this.isAuthenticated()) {
                console.warn('Not authenticated. Cannot fetch remote file.');
                return null;
            }

            const url = `${this.baseURL}/repos/${t.owner}/${t.repo}/contents/${t.path}?ref=${encodeURIComponent(t.branch)}`;
            const response = await axios.get(url, { headers: this.getHeaders() });

            const raw = JSON.parse(atob(response.data.content));
            // Only the frontend file is array-shaped and worth marshaling into
            // the in-memory map. Backend and admin files are object-keyed
            // projections we never edit in place; their SHAs are all we need.
            const parsed = t.label === 'frontend' ? eventsArrayToMap(raw) : raw;
            return {
                content: parsed,
                sha: response.data.sha,
                lastModified: response.data.last_modified || new Date().toISOString(),
            };
        } catch (error) {
            if (error.response?.status === 404) {
                console.warn('File not found at:', t.path);
                return null;
            }
            console.error(`Error getting file content (${t.label}):`, error);
            throw error;
        }
    }

    // Sync local data with remote
    async syncWithRemote() {
        try {
            if (!this.isAuthenticated()) {
                return {
                    success: false,
                    events: null,
                    sha: null,
                    message: 'Not authenticated. Cannot sync with remote repository.'
                };
            }

            const remoteData = await this.getFileContent();
            
            if (!remoteData) {
                return {
                    success: false,
                    events: null,
                    sha: null,
                    message: 'Remote file not found or not accessible. Using local data.'
                };
            }

            return {
                success: true,
                events: remoteData.content,
                sha: remoteData.sha,
                lastModified: remoteData.lastModified,
                message: 'Successfully synced with remote repository'
            };
        } catch (error) {
            console.error('Error syncing with remote:', error);
            return {
                success: false,
                events: null,
                sha: null,
                message: `Failed to sync: ${error.message}`
            };
        }
    }

    // Check if remote has newer changes
    async checkForRemoteChanges(localSha) {
        try {
            if (!this.isAuthenticated()) {
                return { hasChanges: false, message: 'Not authenticated' };
            }

            const remoteData = await this.getFileContent();
            
            if (!remoteData) {
                return { hasChanges: false, message: 'Remote file not found' };
            }

            const hasChanges = localSha && remoteData.sha !== localSha;
            
            return {
                hasChanges: hasChanges,
                remoteSha: remoteData.sha,
                localSha: localSha,
                message: hasChanges ? 'Remote changes detected' : 'No remote changes'
            };
        } catch (error) {
            console.error('Error checking for remote changes:', error);
            return { 
                hasChanges: false, 
                error: error.message,
                message: 'Error checking for remote changes'
            };
        }
    }

    // Low-level: push a single file to a single target branch via Git Data API.
    // `content` is the raw string that will be written to `target.path`.
    async _pushFileToBranch(target, content, commitMessage) {
        // Step 1: current ref SHA
        const refResponse = await axios.get(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/refs/heads/${target.branch}`,
            { headers: this.getHeaders() }
        );
        const currentCommitSha = refResponse.data.object.sha;

        // Step 2: base tree SHA
        const commitResponse = await axios.get(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/commits/${currentCommitSha}`,
            { headers: this.getHeaders() }
        );
        const baseTreeSha = commitResponse.data.tree.sha;

        // Step 3: create blob
        const blobResponse = await axios.post(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/blobs`,
            { content: btoa(content), encoding: 'base64' },
            { headers: this.getHeaders() }
        );
        const newBlobSha = blobResponse.data.sha;

        // Step 4: create tree
        const treeResponse = await axios.post(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/trees`,
            {
                base_tree: baseTreeSha,
                tree: [{ path: target.path, mode: '100644', type: 'blob', sha: newBlobSha }],
            },
            { headers: this.getHeaders() }
        );
        const newTreeSha = treeResponse.data.sha;

        // Step 5: create commit
        const newCommitResponse = await axios.post(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/commits`,
            { message: commitMessage, tree: newTreeSha, parents: [currentCommitSha] },
            { headers: this.getHeaders() }
        );
        const newCommitSha = newCommitResponse.data.sha;

        // Step 6: fast-forward the branch
        await axios.patch(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/refs/heads/${target.branch}`,
            { sha: newCommitSha, force: false },
            { headers: this.getHeaders() }
        );

        return {
            success: true,
            commitSha: newCommitSha,
            commitUrl: newCommitResponse.data.html_url,
        };
    }

    // Public: push the unified events map to frontend, backend, AND admin files
    // in sequence. Each target has its own configured branch (see the
    // `_frontendTarget` / `_backendTarget` / `_adminTarget` helpers) — none of
    // these are hard-coded to "master". Frontend gets the array-shaped
    // events-data.json; backend gets the projected object shape; admin gets a
    // minimal object shape (name + isSub + team + min/max). Same PAT, same
    // commit message.
    async pushToBranches(eventsData, commitMessage) {
        try {
            if (!this.isAuthenticated()) {
                throw new Error('Not authenticated');
            }

            const frontendContent = JSON.stringify(eventsMapToArray(eventsData), null, 2) + '\n';
            const backendContent = JSON.stringify(projectToBackendShape(eventsData), null, 2) + '\n';
            const adminContent = JSON.stringify(projectToAdminShape(eventsData), null, 2) + '\n';

            // Frontend first. If it fails, don't touch backend or admin.
            let frontendResult;
            try {
                frontendResult = await this._pushFileToBranch(
                    this._frontendTarget(),
                    frontendContent,
                    commitMessage
                );
            } catch (err) {
                return {
                    success: false,
                    frontend: { success: false, message: err.response?.data?.message || err.message },
                    backend: { success: false, message: 'Skipped — frontend push failed first.' },
                    admin: { success: false, message: 'Skipped — frontend push failed first.' },
                    message: `Frontend push failed: ${err.response?.data?.message || err.message}`,
                };
            }

            // Then backend. If IT fails, don't touch admin.
            let backendResult;
            try {
                backendResult = await this._pushFileToBranch(
                    this._backendTarget(),
                    backendContent,
                    commitMessage
                );
            } catch (err) {
                return {
                    success: false,
                    frontend: { success: true, ...frontendResult },
                    backend: { success: false, message: err.response?.data?.message || err.message },
                    admin: { success: false, message: 'Skipped — backend push failed.' },
                    message: `Backend push failed AFTER frontend committed. Frontend commit: ${frontendResult.commitUrl}`,
                };
            }

            // Finally admin. If IT fails, surface partial state clearly.
            let adminResult;
            try {
                adminResult = await this._pushFileToBranch(
                    this._adminTarget(),
                    adminContent,
                    commitMessage
                );
            } catch (err) {
                return {
                    success: false,
                    frontend: { success: true, ...frontendResult },
                    backend: { success: true, ...backendResult },
                    admin: { success: false, message: err.response?.data?.message || err.message },
                    message: `Admin push failed AFTER frontend + backend committed. Frontend: ${frontendResult.commitUrl} | Backend: ${backendResult.commitUrl}`,
                };
            }

            return {
                success: true,
                frontend: { success: true, ...frontendResult },
                backend: { success: true, ...backendResult },
                admin: { success: true, ...adminResult },
                commitSha: frontendResult.commitSha,
                commitUrl: frontendResult.commitUrl,
                message: 'Changes pushed to frontend, backend, and admin successfully',
            };
        } catch (error) {
            console.error('Error pushing directly to configured branches:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to push',
            };
        }
    }

    // Low-level: open one PR against `target` with `content` under `target.path`.
    async _openPullRequest(target, content, commitMessage, prTitle) {
        const branchName = `events-update-${Date.now()}`;

        const baseRefResponse = await axios.get(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/refs/heads/${target.branch}`,
            { headers: this.getHeaders() }
        );
        const baseSha = baseRefResponse.data.object.sha;

        await axios.post(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/git/refs`,
            { ref: `refs/heads/${branchName}`, sha: baseSha },
            { headers: this.getHeaders() }
        );

        const currentFile = await this.getFileContent(target);
        const currentSha = currentFile ? currentFile.sha : null;

        const updateData = {
            message: commitMessage,
            content: btoa(content),
            branch: branchName,
        };
        if (currentSha) updateData.sha = currentSha;

        await axios.put(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/contents/${target.path}`,
            updateData,
            { headers: this.getHeaders() }
        );

        const prResponse = await axios.post(
            `${this.baseURL}/repos/${target.owner}/${target.repo}/pulls`,
            {
                title: prTitle,
                head: branchName,
                base: target.branch,
                body: `Automated update of ${target.path}\n\nCommit: ${commitMessage}`,
            },
            { headers: this.getHeaders() }
        );

        return {
            success: true,
            pullRequestNumber: prResponse.data.number,
            pullRequestUrl: prResponse.data.html_url,
        };
    }

    // Public: open three PRs — frontend + backend + admin. All must be merged
    // for the site, API, and admin panel to agree on the events catalog.
    async createPullRequest(eventsData, commitMessage, prTitle) {
        try {
            if (!this.isAuthenticated()) {
                throw new Error('Not authenticated');
            }

            const frontendContent = JSON.stringify(eventsMapToArray(eventsData), null, 2) + '\n';
            const backendContent = JSON.stringify(projectToBackendShape(eventsData), null, 2) + '\n';
            const adminContent = JSON.stringify(projectToAdminShape(eventsData), null, 2) + '\n';

            let frontendPr;
            try {
                frontendPr = await this._openPullRequest(
                    this._frontendTarget(),
                    frontendContent,
                    commitMessage,
                    prTitle
                );
            } catch (err) {
                return {
                    success: false,
                    frontend: { success: false, message: err.response?.data?.message || err.message },
                    backend: { success: false, message: 'Skipped — frontend PR failed first.' },
                    admin: { success: false, message: 'Skipped — frontend PR failed first.' },
                    message: `Frontend PR failed: ${err.response?.data?.message || err.message}`,
                };
            }

            let backendPr;
            try {
                backendPr = await this._openPullRequest(
                    this._backendTarget(),
                    backendContent,
                    commitMessage,
                    prTitle
                );
            } catch (err) {
                return {
                    success: false,
                    frontend: { success: true, ...frontendPr },
                    backend: { success: false, message: err.response?.data?.message || err.message },
                    admin: { success: false, message: 'Skipped — backend PR failed.' },
                    message: `Backend PR failed. Frontend PR still opened: ${frontendPr.pullRequestUrl}`,
                };
            }

            let adminPr;
            try {
                adminPr = await this._openPullRequest(
                    this._adminTarget(),
                    adminContent,
                    commitMessage,
                    prTitle
                );
            } catch (err) {
                return {
                    success: false,
                    frontend: { success: true, ...frontendPr },
                    backend: { success: true, ...backendPr },
                    admin: { success: false, message: err.response?.data?.message || err.message },
                    message: `Admin PR failed. Frontend PR: ${frontendPr.pullRequestUrl} | Backend PR: ${backendPr.pullRequestUrl}`,
                };
            }

            return {
                success: true,
                frontend: { success: true, ...frontendPr },
                backend: { success: true, ...backendPr },
                admin: { success: true, ...adminPr },
                pullRequestNumber: frontendPr.pullRequestNumber,
                pullRequestUrl: frontendPr.pullRequestUrl,
                message: `PRs opened — frontend #${frontendPr.pullRequestNumber}, backend #${backendPr.pullRequestNumber}, admin #${adminPr.pullRequestNumber}`,
            };
        } catch (error) {
            console.error('Error creating pull requests:', error);
            return {
                success: false,
                message: error.message || 'Failed to create pull requests',
            };
        }
    }
}

export default EventManager;